/**
 * Storage durability, and being honest about it (T-205, ADR-0003).
 *
 * `navigator.storage.persist()` is a *request*. Browsers grant it on
 * heuristics — engagement, installation, bookmarking — may refuse, and may
 * revoke later. So the app reports which regime it is actually in rather than
 * assuming the best, and never tells the user their data is safe. It cannot
 * know that.
 */

export type PersistenceState =
  /** Exempt from eviction under storage pressure. As good as this gets. */
  | 'persistent'
  /** Stored, but the browser may evict it. The common case. */
  | 'best-effort'
  /** The browser has no Storage API — nothing can be requested or reported. */
  | 'unsupported'

export interface StorageStatus {
  readonly state: PersistenceState
  /** Bytes in use, where the browser exposes it. */
  readonly usageBytes?: number
  /** Bytes available, where the browser exposes it. */
  readonly quotaBytes?: number
}

function supported(): boolean {
  return (
    typeof navigator !== 'undefined' && typeof navigator.storage?.persisted === 'function'
  )
}

/**
 * Reads the current state without asking for anything.
 *
 * Separate from `requestPersistence` on purpose: displaying the state must
 * never trigger a permission decision. Opening Settings is not consent, and on
 * browsers that prompt, a request fired by a screen the user was only reading
 * gets refused — and a refusal is not retryable.
 */
export async function readStorageStatus(): Promise<StorageStatus> {
  if (!supported()) {
    return { state: 'unsupported' }
  }

  const persisted = await navigator.storage.persisted()
  const state: PersistenceState = persisted ? 'persistent' : 'best-effort'

  if (typeof navigator.storage.estimate !== 'function') {
    return { state }
  }

  try {
    const { usage, quota } = await navigator.storage.estimate()
    return {
      state,
      ...(usage === undefined ? {} : { usageBytes: usage }),
      ...(quota === undefined ? {} : { quotaBytes: quota }),
    }
  } catch {
    // An estimate that fails is not worth failing the whole read for — the
    // persistence state is the part that matters and it is already known.
    return { state }
  }
}

/**
 * Asks to be exempted from eviction.
 *
 * Call this **after** the user has stored something real. Requested on first
 * paint, with no engagement signal to go on, browsers are at their most likely
 * to refuse — and refusal is permanent for the session, so a badly timed ask
 * spends the only attempt available.
 */
export async function requestPersistence(): Promise<PersistenceState> {
  if (!supported() || typeof navigator.storage.persist !== 'function') {
    return 'unsupported'
  }
  if (await navigator.storage.persisted()) {
    return 'persistent'
  }
  try {
    return (await navigator.storage.persist()) ? 'persistent' : 'best-effort'
  } catch {
    return 'best-effort'
  }
}

/** Human-readable bytes. Binary units, because that is what quotas are in. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`
  }
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit] ?? 'TB'}`
}

/**
 * What to tell the user, per state.
 *
 * "Your data is safe" is not a claim this app can make, so none of these say
 * it. What is useful is the actual state and one concrete thing to do about
 * it — which is also what makes the backup nag in T-503 read as reasonable
 * rather than as pestering.
 */
export function describePersistence(state: PersistenceState): {
  readonly label: string
  readonly detail: string
} {
  switch (state) {
    case 'persistent':
      return {
        label: 'Protected',
        detail:
          'The browser has agreed not to delete this data to reclaim space. It can still be removed by clearing site data, and there is no copy anywhere else — keep exporting backups.',
      }
    case 'best-effort':
      return {
        label: 'Best effort',
        detail:
          'The browser may delete this data when space runs low, or after a long gap without opening the app. Installing to your Home Screen makes that much less likely.',
      }
    case 'unsupported':
      return {
        label: 'Unknown',
        detail:
          'This browser does not report whether stored data is protected from deletion. Assume it is not, and export backups regularly.',
      }
  }
}
